/**
 * The one way the browser talks to the server (§0.6 single door).
 *
 * Every call returns a typed result or throws an ApiError carrying the server's
 * own human sentence — screens never invent their own error wording, and a
 * network failure reads the same as a server failure to the person using it.
 */

export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface Options {
  method?: 'GET' | 'POST';
  body?: unknown;
  /**
   * Marks this request as the result of real user interaction.
   *
   * The server advances the cart's 5-minute idle clock ONLY for these (see
   * api/lib/session.ts). Background polling must pass false, otherwise polling
   * would keep an abandoned cart signed in forever.
   */
  userActive?: boolean;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { method = 'GET', body, userActive = method === 'POST' } = opts;

  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (userActive) headers['x-user-active'] = '1';

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
    });
  } catch {
    throw new ApiError('Could not reach the shop system. Check the connection and try again.', 0);
  }

  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    // A non-JSON response means something upstream broke.
    if (!res.ok) throw new ApiError('Something went wrong on our side. Please try again.', res.status);
  }

  const payload = (data ?? {}) as { ok?: boolean; error?: string };

  if (!res.ok || payload.ok === false) {
    throw new ApiError(payload.error ?? 'Something went wrong. Please try again.', res.status);
  }

  return payload as T;
}
